(function () {
	'use strict';

	// Must stay in sync with UNTAGGED_CODE in projectListController.js and in
	// project-tag-folders.component.js.
	var UNTAGGED_CODE = '__dc_untagged__';

	ProjectTagDockController.$inject = ['gettextCatalog'];
	function ProjectTagDockController(gettextCatalog) {
		const vm = this;
		vm.tags = [];
		vm.visible = [];
		vm.selected = {};
		vm.untaggedCode = UNTAGGED_CODE;
		// union: a click adds to the selection. exclusive: a click replaces it.
		// Seeded by the host through the binding; changed from here afterwards.
		vm.mode = 'union';
		// The handed-down selection, held until there are tags to apply it to.
		// Applied once per value.
		var pendingCodes;
		var applied = false;
		var appliedMode = 'union';

		vm.$onChanges = onChanges;
		vm.toggle = toggle;
		vm.setMode = setMode;
		vm.clear = clear;
		vm.selectEvery = selectEvery;
		vm.allSelected = allSelected;
		vm.isSelected = isSelected;
		vm.hasSelection = hasSelection;
		vm.tagLabel = tagLabel;

		function onChanges(changes) {
			let changed = false;

			if (changes['mode']) {
				vm.mode = vm.mode === 'exclusive' ? 'exclusive' : 'union';
				if (vm.mode !== appliedMode) {
					appliedMode = vm.mode;
					changed = true;
				}
			}

			// The host hands back down what it was told, so an echo of the
			// current selection is not a change.
			if (changes['selectedCodes'] && !sameCodes(vm.selectedCodes)) {
				pendingCodes = vm.selectedCodes;
				applied = false;
			}

			if (changes['projects']) {
				vm.tags = buildTags(vm.projects);
				changed = true;
			}

			// Nothing to apply a selection to until the tags exist.
			if (!applied && vm.tags.length) {
				applied = true;
				vm.selected = selectionFrom(pendingCodes);
				changed = true;
			}

			if (changed) {
				refresh();
			}
		}

		// Whether a given list says what is already selected. An unapplied
		// selection matches nothing.
		function sameCodes(codes) {
			if (!applied || !_.isArray(codes)) {
				return false;
			}
			const current = selectedCodes();
			return (
				codes.length === current.length &&
				_.difference(current, codes).length === 0
			);
		}

		// Everything, as the opening state. Not persisted: a default the user
		// has not touched must not harden into a stored selection, or a tag
		// added later would come back silently deselected.
		function selectAll() {
			const selected = {};
			_.each(vm.tags, function (tag) {
				selected[tag.code] = true;
			});
			return selected;
		}

		// undefined is a first visit: open on everything, so the chips read the
		// way the list looks and deselecting one narrows from the whole
		// catalogue. A list is honoured as written, the empty one included -
		// that is a deselect-all.
		function selectionFrom(codes) {
			if (!_.isArray(codes)) {
				return selectAll();
			}
			const selected = {};
			_.each(codes, function (code) {
				if (_.isString(code)) {
					selected[code] = true;
				}
			});
			return selected;
		}

		// Called from the user's own actions only: an untouched default must not
		// harden into stored state. Carries the whole selection rather than what
		// is on screen, so a tag the search hides keeps its answer.
		function emitChange() {
			vm.onChange({ codes: selectedCodes(), mode: vm.mode });
		}

		function isUntagged(project) {
			return !project.grouping_tags || !project.grouping_tags[0];
		}

		// One entry per distinct grouping tag code carried by the projects, plus
		// the untagged bucket when any project has no grouping tag at all. A
		// project holding N grouping tags counts towards N entries.
		function buildTags(projects) {
			const allProjects = projects ? projects : [];
			const byCode = {};
			let untagged = 0;

			_.each(allProjects, function (project) {
				if (isUntagged(project)) {
					untagged++;
					return;
				}
				_.each(project.grouping_tags, function (tag) {
					if (!tag || !tag.code) {
						return;
					}
					if (!byCode[tag.code]) {
						// First occurrence wins for the colour.
						byCode[tag.code] = {
							code: tag.code,
							color: tag.color,
							labelColor: tagLabelColor(tag.color),
							count: 0,
						};
					}
					byCode[tag.code].count++;
				});
			});

			const tags = _.map(_.keys(byCode).sort(compareTagCodes), function (code) {
				return byCode[code];
			});

			if (untagged) {
				tags.push({
					code: UNTAGGED_CODE,
					color: undefined,
					labelColor: undefined,
					count: untagged,
				});
			}

			return tags;
		}

		// In the dock's own order - sorted, untagged last - so the host can lay
		// sections out the way the chips read.
		function selectedTags() {
			return _.filter(vm.tags, function (tag) {
				return !!vm.selected[tag.code];
			});
		}

		// Everything picked, including codes no tag in the current catalogue
		// carries: a search narrows the chips, and what it hides stays picked.
		function selectedCodes() {
			return _.filter(_.keys(vm.selected), function (code) {
				return vm.selected[code];
			});
		}

		// The selection narrowed to the tags on screen. Filtering and the header
		// buttons read this; only persistence reads the whole set.
		function visibleCodes() {
			return _.map(selectedTags(), function (tag) {
				return tag.code;
			});
		}

		function hasSelection() {
			return selectedCodes().length > 0;
		}

		function isSelected(code) {
			return !!vm.selected[code];
		}

		function matches(project, codes) {
			if (isUntagged(project)) {
				return codes.indexOf(UNTAGGED_CODE) > -1;
			}
			return !!_.find(project.grouping_tags, function (tag) {
				return tag && codes.indexOf(tag.code) > -1;
			});
		}

		// Several selected tags are a union. The chips are the whole filter, so
		// selecting none means no tag passes and nothing shows - one answer in
		// either layout. Selecting every tag is the whole catalogue and skips
		// filtering, so a project whose grouping tags carry no code stays in a
		// selection that reads as everything.
		function refresh() {
			const projects = vm.projects ? vm.projects : [];
			const codes = visibleCodes();
			if (!vm.tags.length) {
				// Nothing to select, which is not a deselect-all.
				vm.visible = projects;
			} else if (!codes.length) {
				vm.visible = [];
			} else if (codes.length >= vm.tags.length) {
				vm.visible = projects;
			} else {
				vm.visible = _.filter(projects, function (project) {
					return matches(project, codes);
				});
			}
			// The host renders the cards; what a selection means stays here. What
			// is left of the list and which lanes to cut it into go together -
			// they are one answer, and the host rebuilds from both at once.
			vm.onResult({ projects: vm.visible, tags: selectedTags() });
		}

		function toggle(tag) {
			const wasSelected = !!vm.selected[tag.code];
			if (vm.mode === 'exclusive') {
				vm.selected = {};
			}
			vm.selected[tag.code] = !wasSelected;
			emitChange();
			refresh();
		}

		// Narrowing the mode narrows the selection with it: exclusive keeps the
		// first selected code rather than silently showing a union it can no
		// longer express.
		function setMode(mode) {
			if (mode !== 'union' && mode !== 'exclusive') {
				return;
			}
			vm.mode = mode;
			appliedMode = mode;
			if (mode === 'exclusive') {
				// One tag is all the mode can say, so everything else goes,
				// including the codes with no chip on screen that union keeps.
				const codes = visibleCodes();
				vm.selected = {};
				if (codes.length) {
					vm.selected[codes[0]] = true;
				}
			}
			emitChange();
			refresh();
		}

		function clear() {
			vm.selected = {};
			emitChange();
			refresh();
		}

		// Persisted, unlike the opening state selectAll() serves: this one the
		// user asked for, so a tag added later comes back unselected rather than
		// joining a selection made before it existed. Adds the chips on screen
		// to the selection rather than replacing it, so a search hides tags
		// without dropping them.
		function selectEvery() {
			_.each(vm.tags, function (tag) {
				vm.selected[tag.code] = true;
			});
			emitChange();
			refresh();
		}

		function allSelected() {
			return vm.tags.length > 0 && visibleCodes().length >= vm.tags.length;
		}

		// The untagged bucket's code is a sentinel, not something to display.
		function tagLabel(code) {
			return code === UNTAGGED_CODE
				? gettextCatalog.getString('project.tag.none')
				: code;
		}
	}

	const projectTagDock = {
		restrict: 'E',
		bindings: {
			projects: '<',
			// Opening state, handed down by the host. undefined for a first visit.
			selectedCodes: '<',
			mode: '<',
			onResult: '&',
			// What the user did, for the host to persist. Never the default.
			onChange: '&',
		},
		controller: ProjectTagDockController,
		templateUrl:
			'./src/components/directives/project/project-tag-dock/project-tag-dock.component.html',
		controllerAs: 'vm',
	};

	angular.module('dcApp').component('projectTagDock', projectTagDock);
})();
